import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ME_STATUS, verifyCurrentUserAPI } from "../utils/api/users";
import { installAccessDebugger } from "../access/core/diagnostics";
import SessionNotice from "./SessionNotice";
import ApiNoticeHost from "./ApiNoticeHost";
import { installApiResponseGuard } from "../utils/api/responseGuard";
import { hasGlobalStaffAccess } from "../access/core/scope";
import { sessionAccessSignature } from "../access/core/policy";
import { notifyError } from "../utils/notify";
import { ERROR_MESSAGES } from "../utils/errors/errorMessages";
import { clearReportCache } from "../utils/useReportQuery";

//setting maybe the image of the background
export const Context = createContext(null);

// Stable empty arrays: consumers key effects on these, so a fresh `[]` on
// every render would re-run them.
const NO_IDS = Object.freeze([]);

// Re-verification when the tab regains focus (NA-040) is skipped when the last
// `/me` request started less than this long ago: focus and visibilitychange
// fire together, and quick alt-tabbing must not hammer the API.
export const REVALIDATE_MIN_INTERVAL_MS = 15000;
// After a failed verification the provider retries by itself a few times with
// growing delays, then waits for the user (Retry), the browser coming back
// online, or the next tab focus. Never an endless loop.
export const AUTO_RETRY_DELAYS_MS = Object.freeze([2000, 5000, 10000]);

const readToken = () => {
  try {
    return localStorage.getItem("auth-token");
  } catch {
    return null;
  }
};

// `auth-user` was a cache of the user that nothing reads any more (NA-037). It
// is no longer written; the key is only ever removed (sign-out and start-up).
const removeLegacyUserCache = () => {
  try {
    localStorage.removeItem("auth-user");
  } catch {
    // storage unavailable
  }
};

const clearStoredSession = () => {
  try {
    localStorage.removeItem("auth-token");
  } catch {
    // storage unavailable: the in-memory state below is still reset
  }
  removeLegacyUserCache();
};

// What the access layer and the data fetches depend on. When it changes for a signed-in user (a role change, an
// assignment, global access switched on or off, the account disabled) everything loaded under the old access is
// dropped: the authenticated area remounts and refetches (see accessEpoch below).
const sameUser = (a, b) => {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
};

const ContextProvider = (props) => {
  const [lang, setLang] = useState(localStorage.getItem("lang") || "ar");
  const [isAuthenticated, setIsAuthenticated] = useState(
    !!localStorage.getItem("auth-token"),
  );
  // Never seeded from storage: a stored role must not authorize anything. With
  // a token but no user the access layer reports "loading" and renders no
  // protected UI until `/me` has confirmed the session.
  const [currentUserInfo, setCurrentUserInfo] = useState(null);
  // Why the last verification failed (null = fine). Network, server and
  // unexpected answers never end the session; they are retryable.
  const [sessionError, setSessionError] = useState(null);

  // Bumped whenever the signed-in user's effective access changes. The authenticated area is keyed on it, so
  // restricted data that was loaded before the change is never kept on screen.
  const [accessEpoch, setAccessEpoch] = useState(0);
  const accessSignatureRef = useRef(null);

  const mounted = useRef(false);
  const inFlight = useRef(null); // { token, promise }
  const lastStartedAt = useRef(0);
  const failures = useRef(0);
  const retryTimer = useRef(null);
  const sessionErrorRef = useRef(null);
  sessionErrorRef.current = sessionError;

  const cancelRetry = useCallback(() => {
    if (retryTimer.current) clearTimeout(retryTimer.current);
    retryTimer.current = null;
  }, []);

  // Ends the session in memory and in storage: the token, the legacy cache key,
  // the user (so the access policy turns unauthenticated immediately) and the
  // auth flag.
  const clearSession = useCallback(() => {
    cancelRetry();
    failures.current = 0;
    clearStoredSession();
    clearReportCache(); // report rows of this session must not be readable by the next one
    setSessionError(null);
    setIsAuthenticated(false);
    setCurrentUserInfo(null);
    accessSignatureRef.current = null;
  }, [cancelRetry]);

  // Sign out now (the Sign out button; a token that is already gone).
  const signOut = useCallback(() => {
    clearSession();
    window.location.href = "/";
  }, [clearSession]);

  // Confirms the stored token and loads the current user (role, `between`,
  // `assignedBranch`) from `/me`; that answer is the only source of what the
  // user may do. One request at a time: callers that arrive while one is
  // running share it.
  const verifySession = useCallback(() => {
    const token = readToken();
    if (!token) return Promise.resolve();
    if (inFlight.current && inFlight.current.token === token) {
      return inFlight.current.promise;
    }

    lastStartedAt.current = Date.now();
    cancelRetry();
    const entry = { token, promise: null };
    entry.promise = (async () => {
      const result = await verifyCurrentUserAPI(token);
      if (!mounted.current) return;
      // The token may have been replaced or removed while the request ran.
      if (readToken() !== token) return;

      if (result.status === ME_STATUS.OK) {
        failures.current = 0;
        setSessionError(null);
        setIsAuthenticated(true);
        // Access that changed since the last answer (global access, assignments, role ...) invalidates
        // everything loaded under the old one.
        const signature = sessionAccessSignature(result.user);
        if (accessSignatureRef.current !== null && accessSignatureRef.current !== signature) {
          setAccessEpoch((epoch) => epoch + 1);
        }
        accessSignatureRef.current = signature;
        // Unchanged identity keeps the same object: no re-render, no flicker.
        setCurrentUserInfo((previous) =>
          sameUser(previous, result.user) ? previous : result.user,
        );
      } else if (result.status === ME_STATUS.REJECTED) {
        signOut();
      } else {
        // Undecided: keep the token, authorize nothing new, retry a few times.
        setSessionError(result.status);
        if (failures.current < AUTO_RETRY_DELAYS_MS.length) {
          const delay = AUTO_RETRY_DELAYS_MS[failures.current];
          failures.current += 1;
          retryTimer.current = setTimeout(() => {
            retryTimer.current = null;
            verifySession();
          }, delay);
        }
      }
    })().finally(() => {
      if (inFlight.current === entry) inFlight.current = null;
    });
    inFlight.current = entry;
    return entry.promise;
  }, [cancelRetry, signOut]);

  // A page believes the session is over (a failed fetch, a 401). The server decides, not the page: `/me` is asked
  // once, and only a rejected token (401 / unknown user) ends the session. A user who is still valid but was
  // refused (403), or hit a server problem, stays signed in; their access is refreshed instead.
  const confirming = useRef(null);
  const handleTokenExpiration = useCallback(() => {
    if (!readToken()) return signOut();
    if (confirming.current) return confirming.current;
    confirming.current = Promise.resolve(verifySession()).finally(() => {
      confirming.current = null;
    });
    return confirming.current;
  }, [signOut, verifySession]);

  // Manual retry (banner button): starts over with a fresh retry budget.
  const retrySession = useCallback(() => {
    failures.current = 0;
    return verifySession();
  }, [verifySession]);

  useEffect(() => {
    mounted.current = true;
    removeLegacyUserCache();
    verifySession();
    return () => {
      mounted.current = false;
      cancelRetry();
    };
  }, [verifySession, cancelRetry]);

  // Another tab signed out (or in): follow it, no polling involved.
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key !== null && event.key !== "auth-token") return;
      if (!readToken()) {
        clearSession();
      } else {
        verifySession();
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [clearSession, verifySession]);

  // Re-verify `/me` when the tab becomes active again (NA-040), so a role or
  // assignment change made on the server reaches the access policy without a
  // reload. Event driven (no interval). Throttled, and shared with a request
  // that is already running. `online` retries a failed verification at once.
  useEffect(() => {
    const onActive = () => {
      if (document.visibilityState === "hidden") return;
      if (!readToken() || inFlight.current) return;
      if (Date.now() - lastStartedAt.current < REVALIDATE_MIN_INTERVAL_MS) return;
      verifySession();
    };
    const onOnline = () => {
      if (readToken() && sessionErrorRef.current) retrySession();
    };
    document.addEventListener("visibilitychange", onActive);
    window.addEventListener("focus", onActive);
    window.addEventListener("online", onOnline);
    return () => {
      document.removeEventListener("visibilitychange", onActive);
      window.removeEventListener("focus", onActive);
      window.removeEventListener("online", onOnline);
    };
  }, [verifySession, retrySession]);

  // Authorization lives in src/access (useAccess). The context only exposes
  // who is signed in plus the user's assignments, which the data fetches send
  // to the backend as filters (they are data, not permissions).
  const between = currentUserInfo?.between;
  const assignedBranch = currentUserInfo?.assignedBranch;
  const betweenKey = JSON.stringify(between || []);
  const assignedBranchKey = JSON.stringify(assignedBranch || []);
  // A staff user with global access reaches every restaurant: no assignment filter is sent, the server answers with
  // all of them. (Their `between` stays on the account and applies again the moment global access is switched off.)
  const reachesAll = hasGlobalStaffAccess(currentUserInfo);
  const storeAccess = useMemo(
    () => (!reachesAll && Array.isArray(between) && between.length ? between : NO_IDS),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [betweenKey, reachesAll],
  );
  const branchAccess = useMemo(
    () =>
      Array.isArray(assignedBranch) && assignedBranch.length
        ? assignedBranch
        : NO_IDS,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [assignedBranchKey],
  );

  useEffect(() => {
    installAccessDebugger(() => ({ isAuthenticated, user: currentUserInfo }));
  }, [isAuthenticated, currentUserInfo]);

  // Central handling of API answers (see utils/api/responseGuard.js): 401 -> the same confirmation as above,
  // 403 -> a clear "not allowed" notice, no sign-out, and an access refresh.
  const handlersRef = useRef({});
  handlersRef.current = { handleTokenExpiration, verifySession };
  useEffect(
    () =>
      installApiResponseGuard({
        onUnauthorized: () => handlersRef.current.handleTokenExpiration(),
        onForbidden: () => {
          const lang = handlersRef.current.lang;
          // Text from the shared error catalog (FORBIDDEN); one notice per burst of 403s.
          notifyError(
            { en: ERROR_MESSAGES.FORBIDDEN.en, ar: ERROR_MESSAGES.FORBIDDEN.ar },
            { key: "permission-denied", ttl: 8000 },
          );
          // The refusal may mean the user's access changed: ask the server who they are now.
          handlersRef.current.verifySession();
          return lang;
        },
      }),
    [],
  );

  const contextValue = useMemo(
    () => ({
      lang,
      setLang,
      isAuthenticated,
      setIsAuthenticated,
      handleTokenExpiration,
      signOut,
      accessEpoch,
      currentUserInfo,
      setCurrentUserInfo,
      storeAccess,
      branchAccess,
      sessionError,
      retrySession,
    }),
    [
      lang,
      isAuthenticated,
      handleTokenExpiration,
      signOut,
      accessEpoch,
      currentUserInfo,
      storeAccess,
      branchAccess,
      sessionError,
      retrySession,
    ],
  );

  return (
    <Context.Provider value={contextValue}>
      {props.children}
      <ApiNoticeHost lang={lang} />
      <SessionNotice
        lang={lang}
        error={isAuthenticated ? sessionError : null}
        verified={Boolean(currentUserInfo)}
        onRetry={retrySession}
      />
    </Context.Provider>
  );
};

export default ContextProvider;
